import { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Clock,
  Coins,
  Languages,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import {
  upsertUserSettings,
  UserSettingsPayloadInterface,
} from "../../services/settings/userSettings";
import { handleApi } from "../../utils/handleApi";
import {
  getDefaultTimezoneForCountry,
  getTimezoneSearchText,
  getTimezoneUtcOffset,
  getCurrencySymbol,
} from "../../utils/formatters";
import toast from "react-hot-toast";
import { getCountriesList, getLanguagesList } from "../../const/countryList";
import { useNavigate } from "react-router-dom";

const currencyLabelKeys: Record<string, keyof typeof dictionary> = {
  COP: "colombian-peso",
  USD: "dollar",
  ARS: "argentine-peso",
  BOB: "boliviano",
  BRL: "brazilian-real",
  CAD: "canadian-dollar",
  CLP: "chilean-peso",
  MXN: "mexican-peso",
  PYG: "paraguayan-guarani",
  PEN: "peruvian-sol",
  UYU: "uruguayan-peso",
  VES: "venezuelan-bolivar",
  GTQ: "guatemalan-quetzal",
};

export function ConfigurationPanel() {
  const [country, setCountry] = useState<string>("");
  const {
    language,
    setLanguage,
    timezone: authTimezone,
    setTimezone: setAuthTimezone,
  } = useAuth();
  const [languagePreference, setLanguagePreference] =
    useState<Language>(language);
  const [currency, setCurrency] = useState("COP");
  const [loadingCompleteConfiguration, setLoadingCompleteConfiguration] =
    useState(false);
  const [timezone, setTimezone] = useState<string>(
    authTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [tzSearch, setTzSearch] = useState("");
  const [tzOpen, setTzOpen] = useState(false);
  const tzRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const languagesList = getLanguagesList(language);
  const countries = getCountriesList(language);
  const currentCountry = countries.find((c) => c.name === country);
  const currentLanguageLabel =
    languagesList.find((item) => item.code === languagePreference)?.name ||
    languagePreference;
  const currencyLabelKey = currencyLabelKeys[currency];
  const currencyName = currencyLabelKey
    ? dictionary[currencyLabelKey][language]
    : dictionary["local-currency"][language];
  const timezonesList = Array.from(
    new Set(
      countries
        .map((item) => getDefaultTimezoneForCountry(item.country, ""))
        .filter(Boolean)
    )
  );
  const changeButtonClass =
    "h-10 rounded-2xl border-2 border-[#D9DDE8] bg-[#F4F6FF] px-4 text-sm font-semibold text-[#30368F] shadow-[0_8px_18px_rgba(48,54,143,0.08)] transition-all hover:border-[#4B4BFF] hover:bg-white hover:shadow-[0_12px_24px_rgba(75,75,255,0.14)] active:scale-[0.98]";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tzRef.current && !tzRef.current.contains(e.target as Node)) {
        setTzOpen(false);
        setTzSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = (countryName: string) => {
    const selectedCountry = countries.find((item) => item.name === countryName);
    setCountry(countryName);

    if (!selectedCountry) return;

    setCurrency(selectedCountry.currency);
    setLanguagePreference(selectedCountry.language);
    setTimezone(
      getDefaultTimezoneForCountry(
        selectedCountry.country,
        timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    );
  };

  const handleCompleteConfiguration = async () => {
    const countryCode = currentCountry?.country;
    const languageCode = languagePreference;

    if (!countryCode || !languageCode) return;

    setLoadingCompleteConfiguration(true);

    try {
      const data: UserSettingsPayloadInterface = {
        country: countryCode,
        language: languageCode,
        currency,
        time_zone: timezone,
      };

      const { error } = await handleApi(upsertUserSettings(data), false);

      if (error) {
        toast.error(dictionary["error-completing-configuration"][language]);
        return;
      }

      setLanguage(languageCode);
      setAuthTimezone(timezone);
      navigate("/survey", { replace: true });
    } finally {
      setLoadingCompleteConfiguration(false);
    }
  };

  return (
    <div className="w-full max-w-[620px]">
      <div className="rounded-[24px] border border-[#E4E8F2] bg-white p-5 shadow-[0_24px_70px_rgba(22,31,84,0.12)] sm:rounded-[30px] sm:p-7 md:p-8">
        <div className="mb-6">
          <div className="min-w-0 text-left">
            <h1 className="text-[24px] font-bold leading-[1.12] text-[#111827] sm:text-[28px] md:text-[30px]">
              {dictionary["what-country-do-you-want-to-sell-with-fluxi"][language]}
            </h1>
          </div>
        </div>

        <div className="mb-4">
          <Select value={country} onValueChange={handleCountrySelect}>
            <SelectTrigger className="h-[58px] w-full rounded-[18px] border-2 border-[#DCE1EE] bg-white px-4 text-left text-base font-medium text-[#111827] shadow-sm transition-colors hover:border-[#4B4BFF] focus:ring-2 focus:ring-[#4B4BFF]/20">
              <SelectValue placeholder={dictionary["select-your-country"][language]} />
              <ChevronDown className="ml-auto h-5 w-5 text-[#6B7280]" />
            </SelectTrigger>
            <SelectContent className="z-[100] max-h-[300px] overflow-y-auto rounded-[18px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
              {countries.map((c) => (
                <SelectItem
                  key={c.name}
                  value={c.name}
                  className="cursor-pointer rounded-xl px-4 py-3 text-base font-normal text-[#374151] hover:bg-[#F7F8FF]"
                >
                  <span className="flex items-center gap-3">
                    {c.icon ? (
                      <c.icon width="24" height="24" />
                    ) : (
                      <span className="text-lg">{c.flag}</span>
                    )}
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-3">
          <SettingCard
            icon={<Coins className="h-5 w-5" />}
            label={dictionary["currency"][language]}
            value={
              currentCountry
                ? `${currency} - ${currencyName} (${getCurrencySymbol(currentCountry.country)})`
                : "Selecciona un país"
            }
            muted={!currentCountry}
          />

          <div className="rounded-[18px] border-2 border-[#DCE1EE] bg-white p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 text-left">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
                  <Languages className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#6B7280]">
                    {dictionary["language"][language]}
                  </p>
                  <p className="text-base font-semibold text-[#111827]">
                    {currentLanguageLabel}
                  </p>
                </div>
              </div>
              <Select
                value={languagePreference}
                onValueChange={(value) => setLanguagePreference(value as Language)}
              >
                <SelectTrigger className="h-11 w-full rounded-[14px] border border-[#DCE1EE] bg-white px-3 text-sm font-normal text-[#111827] sm:w-[170px]">
                  <SelectValue />
                  <ChevronDown className="ml-auto h-4 w-4 text-[#6B7280]" />
                </SelectTrigger>
                <SelectContent className="z-[100] rounded-[16px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
                  {languagesList.map((item) => (
                    <SelectItem
                      key={item.code}
                      value={item.code}
                      className="cursor-pointer rounded-xl px-4 py-2.5 text-sm font-normal hover:bg-[#F7F8FF]"
                    >
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative rounded-[18px] border-2 border-[#DCE1EE] bg-white p-4" ref={tzRef}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3 text-left">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#6B7280]">Zona horaria</p>
                  <p className="truncate text-base font-semibold text-[#111827]">
                    {timezone} <span className="text-sm font-normal text-[#6B7280]">{getTimezoneUtcOffset(timezone)}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setTzOpen(true);
                  setTzSearch("");
                }}
                className={changeButtonClass}
              >
                Cambiar
              </button>
            </div>

            {tzOpen && (
              <div className="absolute left-4 right-4 top-[calc(100%-6px)] z-[100] rounded-[18px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
                <input
                  autoFocus
                  className="mb-2 h-10 w-full rounded-[12px] border border-[#DCE1EE] px-3 text-sm outline-none focus:border-[#4B4BFF]"
                  placeholder="Buscar zona horaria"
                  value={tzSearch}
                  onChange={(event) => setTzSearch(event.target.value)}
                />
                <div className="max-h-[220px] overflow-y-auto">
                  {timezonesList
                    .filter((tz) =>
                      getTimezoneSearchText(tz).includes(tzSearch.toLowerCase().trim())
                    )
                    .map((tz) => (
                      <button
                        key={tz}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-[#F7F8FF]"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          setTimezone(tz);
                          setTzOpen(false);
                          setTzSearch("");
                        }}
                      >
                        <span className="truncate text-[#111827]">{tz}</span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-[#6B7280]">
                          {getTimezoneUtcOffset(tz)}
                          {tz === timezone && <Check className="h-4 w-4 text-[#4B4BFF]" />}
                        </span>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleCompleteConfiguration}
          disabled={!currentCountry || loadingCompleteConfiguration}
          className="mt-4 inline-flex min-h-[56px] w-full items-center justify-center rounded-[15px] border border-transparent bg-gradient-to-r from-[#2E378D] to-[#504CFF] px-5 py-3 text-lg font-extrabold leading-none text-white shadow-[0_16px_32px_rgba(41,51,130,0.2)] transition-all duration-200 hover:shadow-[0_18px_38px_rgba(41,51,130,0.24)] active:scale-[0.99] disabled:cursor-not-allowed disabled:border-[#E5E7EE] disabled:bg-none disabled:bg-white disabled:text-[#B9B9BD] disabled:shadow-none sm:mt-6 sm:text-[22px]"
        >
          {loadingCompleteConfiguration ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            "Continuar"
          )}
        </button>
      </div>
    </div>
  );
}

function SettingCard({
  icon,
  label,
  value,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="rounded-[18px] border-2 border-[#DCE1EE] bg-white p-4">
      <div className="flex items-center gap-3 text-left">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#6B7280]">{label}</p>
          <p
            className={`truncate text-base font-semibold ${
              muted ? "text-[#9CA3AF]" : "text-[#111827]"
            }`}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}
